import { FC } from "react";
import {
  Button,
  Modal,
  TextAreaInput,
  TextInput,
} from "@monolith-forensics/monolith-ui";
import { useForm } from "@mantine/form";
import { useSnackbar } from "notistack";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CaseReportsAPI from "@/api/cases/reports";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import {
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { CaseReportsQuery } from "../caseReports.query";

interface CreateCaseReportModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  caseId: string | number;
}

export const CreateCaseReportModal: FC<CreateCaseReportModalProps> = ({
  show,
  onClose,
  onSubmit,
  caseId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: {
      name: "",
      description: "",
    },
    validate: {
      name: (value) =>
        !value || value.trim() === "" ? "Case Report name is required" : null,
    },
  });

  const createMutation = useMutation({
    mutationFn: (params: {
      case_id: string | number;
      name: string;
      description?: string;
    }) => CaseReportsAPI.createCaseReport(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CaseReportsQuery.queryKeys.base,
      });

      enqueueSnackbar(`Case Report "${form.values.name}" created`, {
        variant: "success",
      });

      LoggingAPI.createLog({
        case_id: caseId,
        user_id: currentUser.user_id,
        message: `Created case report: ${form.values.name}.`,
        event: "create_case_report",
        object_type: "case_report",
      });

      form.reset();
      onSubmit?.();
      onClose();
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while creating this case report. Please try again.",
        { variant: "error" }
      );
    },
  });

  const handleSubmit = () => {
    if (!form.validate().hasErrors) {
      createMutation.mutate({
        case_id: caseId,
        name: form.values.name,
        description: form.values.description,
      });
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal open={show} onClose={handleCancel}>
      <FormWrapper width={500}>
        <FormTitle>Create Case Report</FormTitle>
        <TextInput
          size="sm"
          variant="outlined"
          label="Case Report Name"
          required
          placeholder="Enter case report name"
          {...form.getInputProps("name")}
        />
        <TextAreaInput
          size="sm"
          variant="outlined"
          label="Description"
          placeholder="Enter a description for this report"
          minRows={4}
          maxRows={10}
          {...form.getInputProps("description")}
        />
        <ButtonMenu>
          <Button size="xs" variant="subtle" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            size="xs"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
          >
            Start Report
          </Button>
        </ButtonMenu>
      </FormWrapper>
    </Modal>
  );
};
